import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import toast from "react-hot-toast";
import { loginFlow, verifyOtp } from '../redux/slices/authSlice';
import { setQrCode, validateQrCode } from '../redux/slices/qrSlice';
import { getT } from '../i18n/translations';



const ErrIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <circle cx="6.5" cy="6.5" r="6" stroke="#e05252" strokeWidth="1.3" />
    <path d="M6.5 3.5v3.5" stroke="#e05252" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="6.5" cy="9.5" r="0.7" fill="#e05252" />
  </svg>
);


const MobileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4a7c59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5" />
  </svg>
);

// ── Top-bar QR icon (click karke manual QR entry popup khulta hai) ──
const QrIcon = ({ size = 22, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <line x1="14" y1="14" x2="14" y2="14.01" />
    <line x1="14" y1="18" x2="14" y2="18.01" />
    <line x1="18" y1="14" x2="18" y2="14.01" />
    <line x1="18" y1="18" x2="21" y2="18" />
    <line x1="21" y1="14" x2="21" y2="21" />
    <line x1="14" y1="21" x2="18" y2="21" />
  </svg>
);

const TermsModal = ({ onClose, onAccept, t }) => {
  const paragraphs = [
    t.terms_para1,
    t.terms_para2,
    t.terms_para3,
    t.terms_para4,
    t.terms_para5,
  ];

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 999, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 32px', width: '100%', maxWidth: '448px', maxHeight: '80vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <p style={{ fontSize: '15px', fontWeight: '700', color: '#1a2e1a', margin: 0 }}>{t.terms_title}</p>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#5a7a5a', lineHeight: 1, padding: '0 4px' }}>&#10005;</button>
        </div>
        <p style={{ fontSize: '11px', fontWeight: '700', color: '#2d5a2d', marginBottom: '12px', letterSpacing: '0.5px' }}>
          {t.terms_subtitle}
        </p>
        {paragraphs.map((text, i) => (
          <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#2d5a2d', minWidth: '18px' }}>{i + 1}.</span>
            <p style={{ fontSize: '12px', color: '#3a4e3a', lineHeight: '1.7', margin: 0 }}>{text}</p>
          </div>
        ))}

        <button
          onClick={onAccept}
          style={{ width: '100%', padding: '13px', marginTop: '8px', backgroundColor: '#2d5a2d', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
        >
          {t.terms_close_btn}
        </button>
      </div>
    </div>
  );
};

const Login = () => {
  const [manualQrCode, setManualQrCode] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const t = getT();
  const [bg, setBg] = useState('/banner1.png');

  useEffect(() => {
    const updateImage = () => {
      if (window.innerWidth < 768) {
        setBg('/banner1.png');
      } else {
        setBg('/banner2.jpg');
      }
    };
    updateImage();
    window.addEventListener('resize', updateImage);
    return () => window.removeEventListener('resize', updateImage);
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, isNewUser } = useSelector((state) => state.auth);
  const existingCode = useSelector((state) => state.qr.code);
  const qrValidation = useSelector((state) => state.qr.validation);
  const savedCode = localStorage.getItem('qrCode');

  // ── Manual QR code submit (popup ke andar wala input) ──
  // Ye ab dobara validateQrCode dispatch karta hai, isliye
  // agar naya code bhi galat nikla to popup wapas khulega (useEffect neeche dekhiye),
  // aur agar valid nikla to popup band ho jayega aur wahi qrCode signup ke liye use hoga.
  const handleManualQrSubmit = () => {
    const code = manualQrCode.trim();
    if (!code) {
      toast.error(t.enterQrCode || 'QR code enter karein');
      return;
    }
    setShowQrPopup(false);
    dispatch(setQrCode(code));
    localStorage.setItem('qrCode', code);
    dispatch(validateQrCode(code));
    setManualQrCode('');
  };

  const isQrChecking = qrValidation.status === 'loading';
  const isBusy = loading || isQrChecking;

  const [step, setStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpError, setOtpError] = useState('');

  // ── QR verification popup
  const [showQrPopup, setShowQrPopup] = useState(false);
  const [qrPopupMessage, setQrPopupMessage] = useState('');

  useEffect(() => {
    if (!existingCode && savedCode) dispatch(setQrCode(savedCode));
  }, []);

  // ── Login page pe aate hi saved QR code validate karo
  useEffect(() => {
    const code = existingCode || savedCode;
    if (code) {
      dispatch(validateQrCode(code));
    }
  }, [existingCode]);

  // ── validation result aane pe popup dikhao agar invalid hai
  // Ye useEffect har baar chalega jab bhi validateQrCode dispatch hoga —
  // chahe wo pehli baar ho (saved QR) ya manual submit ke baad (naya QR).
  // Isliye galat → error → naya try → phir galat → error, ye loop yahin se control hota hai.
  useEffect(() => {
    if (qrValidation.status === 'done' && qrValidation.valid === false) {
      const reasonMessage = {
        invalid: t.home_errInvalid || 'Yeh QR code valid nahi hai.',
        used: t.home_errUsed || 'Yeh QR code pehle hi use ho chuka hai.',
        assigned: t.home_errAssignedOther || 'Yeh QR code kisi aur ko assign ho chuka hai.',
        expired: t.home_errExpired || 'Yeh QR code expire ho chuka hai.',
      };
      setQrPopupMessage(reasonMessage[qrValidation.reason] || t.home_errInvalid);
      setShowQrPopup(true);
    }
    // Agar valid: true aaya, to popup apne aap nahi khulega — user aage badh sakta hai.
  }, [qrValidation]);

  // ── Top-bar QR icon click: manual entry popup kholta hai (bina kisi error message ke) ──
  const handleOpenQrPopup = () => {
    setQrPopupMessage(''); // koi error nahi — user khud se open kar raha hai
    setManualQrCode('');
    setShowQrPopup(true);
  };

  const handlePhoneSubmit = async () => {
    if (phoneNumber.length !== 10) {
      toast.error(t.invalidMobile);
      return;
    }
    const res = await dispatch(loginFlow(phoneNumber));
    if (res.meta.requestStatus === 'fulfilled') {
      toast.success(t.otpSentTo + ' ' + phoneNumber);
      setStep('otp');
    } else {
      const errorMessages = {
        NEED_VALID_QR: t.needValidQr || 'Signup ke liye valid QR code scan karna zaroori hai.',
        QR_USED: t.home_errUsed || 'Yeh QR code pehle hi use ho chuka hai.',
        QR_ASSIGNED: t.home_errAssignedOther || 'Yeh QR code kisi aur ko assign ho chuka hai.',
        QR_EXPIRED: t.home_errExpired || 'Yeh QR code expire ho chuka hai.',
      };
      toast.error(errorMessages[res.payload] || res.payload);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');
    if (value && index < 3) document.getElementById('otp-' + (index + 1))?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById('otp-' + (index - 1))?.focus();
    }
  };

  const handleOtpSubmit = async () => {
    const finalOtp = otp.join('');
    if (finalOtp.length !== 4) {
      setOtpError(t.otpError);
      return;
    }
    const res = await dispatch(verifyOtp({ otp: finalOtp, mobile: phoneNumber }));
    if (res.meta.requestStatus === 'fulfilled') {
      navigate(isNewUser ? '/profile-setup' : '/home');
    } else {
      setOtpError(t.invalidOtp);
    }
  };

  const handleResendOtp = async () => {
    const res = await dispatch(loginFlow(phoneNumber));
    if (res.meta.requestStatus === 'fulfilled') toast.success(t.otpResent);
  };

  const isSubmitDisabled = isBusy || phoneNumber.length !== 10 || !termsAccepted;
  const isVerifyDisabled = isBusy || otp.join('').length !== 4;

  return (
    <React.Fragment>
      {/* ── GLOBAL LOADER (koi bhi API chalte waqt) ── */}
      {isBusy && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(2px)',
          }}
        >
          <Loader2 className="animate-spin" size={48} color="#2d5a2d" />
        </div>
      )}

      {showTerms && (
        <TermsModal
          onClose={() => setShowTerms(false)}
          onAccept={() => { setTermsAccepted(true); setShowTerms(false); }}
          t={t}
        />
      )}

      {/* ── QR STATUS POPUP (with manual QR entry) ── */}
      {showQrPopup && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', width: '88%', maxWidth: '380px', borderRadius: '16px', padding: '24px', textAlign: 'center', position: 'relative' }}>
            <button
              onClick={() => setShowQrPopup(false)}
              style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', color: '#9ca3af', fontSize: '20px', cursor: 'pointer' }}
            >✖</button>

            {/* Agar koi error message hai to error icon + title dikhao, warna neutral QR icon + title */}
            {qrPopupMessage ? (
              <React.Fragment>
                <div style={{ width: 64, height: 64, margin: '0 auto 16px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#ef4444' }}>✖</div>
                <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#1a2e1a' }}>
                  {t.home_verificationFailed || 'QR Verification Failed'}
                </h2>
                <p style={{ fontSize: 14, color: 'red', marginBottom: 20 }}>{qrPopupMessage}</p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <div style={{ width: 64, height: 64, margin: '0 auto 16px', borderRadius: '50%', backgroundColor: '#e6f0e6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <QrIcon size={28} color="#2d5a2d" />
                </div>
                <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#1a2e1a' }}>
                  {t.enterQrCodeTitle || 'Enter QR Code'}
                </h2>
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
                  {t.enterQrCodeSubtitle || 'Apna QR code manually daal kar submit karein.'}
                </p>
              </React.Fragment>
            )}

            {/* Manual QR input */}
            <input
              type="text"
              value={manualQrCode}
              onChange={(e) => setManualQrCode(e.target.value)}
              placeholder={t.enterQrCodePlaceholder || 'Enter QR code value'}
              style={{
                width: '100%',
                padding: '12px 14px',
                marginBottom: '14px',
                borderRadius: '10px',
                border: '1.5px solid #e0e8e0',
                fontSize: '14px',
                outline: 'none',
                fontFamily: "'Poppins', sans-serif",
                boxSizing: 'border-box',
              }}
            />

            <button
              disabled={isQrChecking}
              onClick={handleManualQrSubmit}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                backgroundColor: isQrChecking ? '#8fad8f' : '#2d5a2d',
                color: '#fff',
                border: 'none',
                fontWeight: 600,
                cursor: isQrChecking ? 'not-allowed' : 'pointer',
              }}
            >
              {isQrChecking ? (t.checking || 'Checking...') : (t.submitQrCode || 'Submit QR Code')}
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          backgroundColor: '#f0f4f0',
          backgroundImage: 'radial-gradient(circle, #c8d8c8 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '420px',
            height: '100vh',
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Background image */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '18px',
              backgroundImage: `url(${bg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'top center',
              backgroundRepeat: 'no-repeat',
              zIndex: 0,
            }}
          />

          {/* ── Top-bar QR icon button ── */}
          <button
            onClick={handleOpenQrPopup}
            title={t.enterQrCodeTitle || 'Enter QR Code'}
            style={{
              position: 'absolute',
              top: '56px',
              right: '16px',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: 'rgba(0,0,0,0.35)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backdropFilter: 'blur(2px)',
            }}
          >
            <QrIcon size={20} color="#fff" />
          </button>

          {/* Content */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              padding: '0 20px',
            }}
          >
            {step === 'phone' && (
              <React.Fragment>
                <div style={{ textAlign: 'center', marginTop: '36px' }}>
                  <h1 style={{ fontSize: '26px', fontWeight: '700', color: '#fff', marginTop: "20px" }}>
                    {t.home_welcome}
                  </h1>
                </div>

                <div style={{ flex: 0.3 }} />

                <div
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '18px',
                    padding: '24px 20px',
                    width: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    marginBottom: '32px',
                  }}
                >
                  <p style={{ fontSize: '10px', fontWeight: '600', color: '#8a9e8a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px', marginTop: 0 }}>
                    {t.mobileLabel}
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1.5px solid #e0e8e0',
                      borderRadius: '12px',
                      padding: '12px 14px',
                      gap: '10px',
                    }}
                  >
                    <MobileIcon />
                    <div style={{ width: '1px', height: '20px', backgroundColor: '#d0dcd0' }} />
                    <input
                      type="tel"
                      inputMode="numeric"
                      placeholder={t.mobilePlaceholder}
                      value={phoneNumber}
                      maxLength={10}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        outline: 'none',
                        border: 'none',
                        fontSize: '15px',
                        color: '#1a2e1a',
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px', marginBottom: '20px' }}>
                    <input
                      type="checkbox"
                      id="terms"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3a6b3a' }}
                    />
                    <label htmlFor="terms" style={{ fontSize: '13px', color: '#3a4e3a', cursor: 'pointer' }}>
                      {t.termsText}{' '}
                      <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                        style={{ color: '#3a6b3a', fontWeight: '600', textDecoration: 'none' }}
                      >
                        {t.termsLink}
                      </a>
                    </label>
                  </div>

                  <button
                    disabled={isSubmitDisabled}
                    onClick={handlePhoneSubmit}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#fff',
                      border: 'none',
                      cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                      backgroundColor: isSubmitDisabled ? '#8fad8f' : '#2d5a2d',
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    {loading ? t.sendingOtp : t.submit}
                    {!loading && <span>&#8594;</span>}
                  </button>
                </div>
              </React.Fragment>
            )}

            {step === 'otp' && (
              <React.Fragment>
                <div style={{ flex: 0.3 }} />

                <div
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '18px',
                    padding: '24px 20px',
                    width: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    marginBottom: '32px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <button
                      onClick={() => { setStep('phone'); setOtp(['', '', '', '']); setOtpError(''); }}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                    >
                      <ArrowLeft size={22} color="#1a2e1a" />
                    </button>
                    <h2 style={{ flex: 1, textAlign: 'center', fontSize: '16px', fontWeight: '600', color: '#1a2e1a', margin: 0, marginRight: '28px' }}>
                      {t.loginWithOtp}
                    </h2>
                  </div>

                  <p style={{ textAlign: 'center', fontSize: '13px', color: '#5a7a5a', marginBottom: '20px', lineHeight: '1.6' }}>
                    {t.otpSentMessage} {phoneNumber}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
                    {otp.map((v, i) => (
                      <input
                        key={i}
                        id={'otp-' + i}
                        type="text"
                        maxLength={1}
                        inputMode="numeric"
                        value={v}
                        onChange={(e) => handleOtpChange(i, e.target.value.replace(/\D/g, ''))}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '12px',
                          textAlign: 'center',
                          fontSize: '22px',
                          fontWeight: '700',
                          color: '#1a2e1a',
                          outline: 'none',
                          border: otpError ? '1.5px solid #f87171' : '1.5px solid #e0e8e0',
                          backgroundColor: otpError ? '#fef2f2' : '#f8faf8',
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      />
                    ))}
                  </div>

                  {otpError && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px', color: '#ef4444', marginBottom: '12px' }}>
                      <ErrIcon /> {otpError}
                    </div>
                  )}

                  <button
                    disabled={isVerifyDisabled}
                    onClick={handleOtpSubmit}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#fff',
                      border: 'none',
                      cursor: isVerifyDisabled ? 'not-allowed' : 'pointer',
                      backgroundColor: isVerifyDisabled ? '#8fad8f' : '#2d5a2d',
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    {loading ? t.verifying : t.login}
                  </button>

                  <div style={{ textAlign: 'center', marginTop: '14px' }}>
                    <button
                      onClick={handleResendOtp}
                      style={{ background: 'transparent', border: 'none', fontSize: '13px', fontWeight: '600', color: '#3a6b3a', textDecoration: 'underline', cursor: 'pointer', fontFamily: "'Poppins', sans-serif" }}
                    >
                      {t.resendOtp}
                    </button>
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
          <div className='absolute bottom-0 p-2 left-0 w-full' style={{
            backgroundColor: 'rgba(0,0,0,0.65)',
          }}>
            <p className=" w-full text-[10px] tracking-[1.2px] text-white uppercase text-center">© {new Date().getFullYear()} Gencrest</p>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Login;